import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isAddress, formatEther, parseEther } from "viem";
import { usePublicClient, useWriteContract } from "wagmi";
import NftCard from "../components/common/NftCard";
import Panel from "../components/common/Panel";
import Seo from "../components/common/Seo";
import chronosArtNftAbi from "../abi/ChronosArtNFT.json";
import nftSaleAbi from "../abi/NftSaleContract.json";
import auctionAbi from "../abi/AuctionContract.json";
import { t } from "../i18n/messages";
import {
  apiNotifyAuctionTx,
  apiNotifyShopTx,
  apiGetMarketNfts,
  apiGetUserAuctions,
  apiSyncUserNftHoldings,
  apiUserNftListings,
} from "../api/backend";
import { useAppState } from "../state/AppStateProvider";
import {
  AUCTION_CONTRACT_ADDRESS,
  AUCTION_QUOTE_SYMBOL,
  CHECKIN_CONTRACT_ADDRESS,
  DEFAULT_NFT_ADDRESS,
  NFT_SALE_CONTRACT_ADDRESS,
  USDT_CONTRACT_ADDRESS,
} from "../utils/contractConstants";
import { buildPath } from "../utils/routing";
import { formatAmount } from "../utils/format";
import { resolveNftGalleryImages, resolveNftImage } from "../utils/nftMedia";

const CHECKIN_CONTRACT_ABI = [
  {
    type: "function",
    name: "checkIn",
    stateMutability: "nonpayable",
    inputs: [],
    outputs: [],
  },
];

const ERC165_ABI = [
  {
    type: "function",
    name: "supportsInterface",
    stateMutability: "view",
    inputs: [{ name: "interfaceId", type: "bytes4" }],
    outputs: [{ name: "", type: "bool" }],
  },
];

const ERC721_ENUMERABLE_ABI = [
  {
    type: "function",
    name: "tokenOfOwnerByIndex",
    stateMutability: "view",
    inputs: [
      { name: "owner", type: "address" },
      { name: "index", type: "uint256" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
];

const ERC20_ABI = [
  {
    type: "function",
    name: "allowance",
    stateMutability: "view",
    inputs: [
      { name: "owner", type: "address" },
      { name: "spender", type: "address" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    type: "function",
    name: "approve",
    stateMutability: "nonpayable",
    inputs: [
      { name: "spender", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ name: "", type: "bool" }],
  },
];

const ERC721_ENUMERABLE_INTERFACE_ID = "0x780e9d63";
const USDT_CANCEL_APPROVAL_WEI = parseEther("1000");

function toErrorMessage(error) {
  // Walk the cause chain to find the most specific revert reason
  const cause = error?.cause;
  if (cause?.reason) return String(cause.reason);
  if (cause?.shortMessage) return String(cause.shortMessage);
  if (cause?.message) return String(cause.message);
  return String(error?.shortMessage || error?.message || error || "");
}

function normalizeRpcError(error, locale) {
  const rawMessage = toErrorMessage(error);
  const isRpcFailure =
    /rpc|http request failed|failed to fetch|network ?error|timeout|429|503|thirdweb/i.test(
      rawMessage,
    );

  if (isRpcFailure) {
    return `${t(locale, "profileMsgRpcUnavailableShort")} ${t(locale, "profileMsgRpcUnavailableHint")}`;
  }

  return rawMessage;
}

function normalizeLowerAddress(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

export default function ProfilePage({ locale }) {
  const queryClient = useQueryClient();
  const { writeContractAsync } = useWriteContract();
  const publicClient = usePublicClient();
  const {
    walletConnected,
    walletAddress,
    checkinLog,
    dashboard,
    runCheckinApi,
  } = useAppState();

  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [queryNftAddress, setQueryNftAddress] = useState(DEFAULT_NFT_ADDRESS);
  const [holdingsStatus, setHoldingsStatus] = useState("idle");
  const [holdingsMessage, setHoldingsMessage] = useState("");
  const [tokenIds, setTokenIds] = useState([]);
  const [listingsMap, setListingsMap] = useState({});
  const [auctionsMap, setAuctionsMap] = useState({});
  const autoHoldingsQueryKeyRef = useRef("");
  const [tradeStatus, setTradeStatus] = useState("idle");
  const [tradeMessage, setTradeMessage] = useState("");
  const [tradeAction, setTradeAction] = useState({ type: "", tokenId: "" });
  const listTokenInFlightRef = useRef(false);
  const [auctionStatus, setAuctionStatus] = useState("idle");
  const [auctionMessage, setAuctionMessage] = useState("");
  const [auctionAction, setAuctionAction] = useState({
    type: "",
    auctionId: "",
    tokenId: "",
  });
  const [listingModal, setListingModal] = useState({
    open: false,
    mode: "",
    tokenId: "",
    price: "",
    durationHours: "12h",
  });
  const isCheckinProcessing = status === "loading";
  const normalizedQueryNftAddress = String(queryNftAddress || "")
    .trim()
    .toLowerCase();
  const setAuctionFeedback = (s, m) => {
    setAuctionStatus(s);
    setAuctionMessage(m);
  };
  const isTradeProcessing = tradeStatus === "loading";
  const isAuctionProcessing = auctionStatus === "loading";

  const nftCatalogQuery = useQuery({
    queryKey: ["profile", "nft-catalog", normalizedQueryNftAddress, locale],
    queryFn: async () => {
      const result = await apiGetMarketNfts(500, locale);
      return Array.isArray(result?.nfts) ? result.nfts : [];
    },
    enabled: Boolean(isAddress(queryNftAddress)),
    staleTime: 60_000,
    retry: 1,
  });

  const nftCatalogByTokenId = useMemo(() => {
    const map = {};
    const rows = Array.isArray(nftCatalogQuery.data)
      ? nftCatalogQuery.data
      : [];
    rows.forEach((row) => {
      const nftAddress = String(
        row?.nftAddress ?? row?.nft_address ?? row?.nft ?? "",
      )
        .trim()
        .toLowerCase();
      if (
        normalizedQueryNftAddress &&
        nftAddress &&
        nftAddress !== normalizedQueryNftAddress
      ) {
        return;
      }
      const tokenId = String(
        row?.tokenId ?? row?.token_id ?? row?.tokenid ?? "",
      ).trim();
      if (!tokenId) return;
      map[tokenId] = {
        image: resolveNftImage(row),
        galleryImages: resolveNftGalleryImages(row),
        name: String(row?.name || "").trim(),
      };
    });
    return map;
  }, [nftCatalogQuery.data, normalizedQueryNftAddress]);

  // Calculate today's date key
  const todayKey = useMemo(() => {
    const today = new Date();
    return `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, "0")}-${String(today.getUTCDate()).padStart(2, "0")}`;
  }, []);

  // Check if already checked in today
  const alreadyCheckedInToday = useMemo(() => {
    return walletConnected && dashboard?.lastCheckinDate === todayKey;
  }, [walletConnected, dashboard?.lastCheckinDate, todayKey]);

  const runListToken = async (tokenIdInput, priceInput = "") => {
    if (isTradeProcessing || listTokenInFlightRef.current) return false;
    if (!walletConnected || !walletAddress) {
      setTradeStatus("error");
      setTradeMessage(t(locale, "profileMsgConnectWalletLogin"));
      return false;
    }
    if (!isAddress(queryNftAddress)) {
      setTradeStatus("error");
      setTradeMessage(t(locale, "profileMsgInvalidNftAddress"));
      return false;
    }
    const tokenIdText = String(tokenIdInput ?? "").trim();
    if (!tokenIdText) {
      setTradeStatus("error");
      setTradeMessage(t(locale, "profileMsgNoTokenId"));
      return false;
    }
    const price = String(priceInput || "").trim();
    let priceWei = 0n;
    try {
      priceWei = parseEther(price);
    } catch {
      priceWei = 0n;
    }
    if (!price || priceWei <= 0n) {
      setTradeStatus("error");
      setTradeMessage(t(locale, "profileMsgInvalidPrice"));
      return false;
    }
    if (!publicClient) {
      setTradeStatus("error");
      setTradeMessage(t(locale, "profileMsgRpcUnavailableShort"));
      return false;
    }

    try {
      listTokenInFlightRef.current = true;
      const tokenId = BigInt(tokenIdText);
      const nftAddress = queryNftAddress.trim();
      const apiNftAddress = nftAddress.toLowerCase();
      const saleSpenderAddress = NFT_SALE_CONTRACT_ADDRESS;
      const isSaleApproved = async () => {
        const [approvedResult, approvedForAllResult] =
          await publicClient.multicall({
            contracts: [
              {
                address: nftAddress,
                abi: chronosArtNftAbi,
                functionName: "getApproved",
                args: [tokenId],
              },
              {
                address: nftAddress,
                abi: chronosArtNftAbi,
                functionName: "isApprovedForAll",
                args: [walletAddress, saleSpenderAddress],
              },
            ],
            allowFailure: true,
          });

        const approvedAddress =
          approvedResult?.status === "success" ? approvedResult.result : "";
        const approvedForAll =
          approvedForAllResult?.status === "success"
            ? approvedForAllResult.result
            : false;

        return (
          normalizeLowerAddress(approvedAddress) ===
            normalizeLowerAddress(saleSpenderAddress) || Boolean(approvedForAll)
        );
      };
      setTradeAction({ type: "list", tokenId: tokenIdText });
      setTradeStatus("loading");
      setTradeMessage(t(locale, "profileMsgAuthorizingListing"));

      let hasApproval = await isSaleApproved();
      if (!hasApproval) {
        const approveTx = await writeContractAsync({
          account: walletAddress,
          address: nftAddress,
          abi: chronosArtNftAbi,
          functionName: "approve",
          args: [saleSpenderAddress, tokenId],
        });

        await publicClient.waitForTransactionReceipt({
          hash: approveTx,
          timeout: 120_000,
        });

        for (let retry = 0; retry < 3; retry += 1) {
          hasApproval = await isSaleApproved();
          if (hasApproval) break;
          await new Promise((resolve) => setTimeout(resolve, 600));
        }
      }

      if (!hasApproval) {
        throw new Error(
          t(
            locale,
            "profileMsgApproveNotDetected",
            "Approval was not detected on-chain. Please retry listing.",
          ),
        );
      }

      const expectedSaleQuote = normalizeLowerAddress(USDT_CONTRACT_ADDRESS);
      try {
        const quoteAllowed = await publicClient.readContract({
          address: NFT_SALE_CONTRACT_ADDRESS,
          abi: nftSaleAbi,
          functionName: "quoteWhitelist",
          args: [USDT_CONTRACT_ADDRESS],
        });
        if (!quoteAllowed) {
          throw new Error(
            t(
              locale,
              "profileMsgSaleQuoteNotWhitelisted",
              `USDT quote is not whitelisted on sale contract. expected=${expectedSaleQuote}`,
            ),
          );
        }
      } catch (quoteReadError) {
        const quoteMessage = String(
          quoteReadError?.message || quoteReadError || "",
        );
        if (/not whitelisted/i.test(quoteMessage)) {
          throw quoteReadError;
        }
        console.warn(
          "[market/list] read sale quoteWhitelist failed, continue listing",
          quoteReadError,
        );
      }

      setTradeMessage(
        t(
          locale,
          "profileMsgListSubmitting",
          "Submitting listing transaction...",
        ),
      );

      const { request: simulatedSale } = await publicClient.simulateContract({
        account: walletAddress,
        address: NFT_SALE_CONTRACT_ADDRESS,
        abi: nftSaleAbi,
        functionName: "sale",
        args: [
          nftAddress,
          tokenId,
          priceWei,
          USDT_CONTRACT_ADDRESS,
          // Legacy native/BNB quote:
          // zeroAddress,
        ],
      });

      const saleTx = await writeContractAsync({
        ...simulatedSale,
      });

      await publicClient.waitForTransactionReceipt({
        hash: saleTx,
        timeout: 120_000,
      });

      await apiNotifyShopTx({
        action: "list",
        txHash: saleTx,
        source: "profile-list-token",
        nftAddress: apiNftAddress,
        tokenId: String(tokenId),
        price: String(price).trim(),
      });

      await queryClient.invalidateQueries({
        queryKey: [
          "profile",
          "nft-listings",
          walletAddress.toLowerCase(),
          apiNftAddress,
        ],
      });

      setTradeStatus("success");
      setTradeMessage(
        `Token #${tokenIdText} ${t(locale, "profileMsgListSuccess")}${saleTx}`,
      );
      await runHoldingsQuery();
      return true;
    } catch (error) {
      setTradeStatus("error");
      setTradeMessage(
        `${t(locale, "profileMsgListFailed")}${normalizeRpcError(error, locale)}`,
      );
      return false;
    } finally {
      listTokenInFlightRef.current = false;
      setTradeAction({ type: "", tokenId: "" });
    }
  };

  const runOffloadToken = async (tokenIdInput) => {
    if (isTradeProcessing) return;
    if (!walletConnected || !walletAddress) {
      setTradeStatus("error");
      setTradeMessage(t(locale, "profileMsgConnectWalletLogin"));
      return;
    }
    if (!isAddress(queryNftAddress)) {
      setTradeStatus("error");
      setTradeMessage(t(locale, "profileMsgInvalidNftAddress"));
      return;
    }
    const tokenIdText = String(tokenIdInput ?? "").trim();
    if (!tokenIdText) {
      setTradeStatus("error");
      setTradeMessage(t(locale, "profileMsgNoTokenId"));
      return;
    }
    if (!publicClient) {
      setTradeStatus("error");
      setTradeMessage(t(locale, "profileMsgRpcUnavailableShort"));
      return;
    }

    try {
      const tokenId = BigInt(tokenIdText);
      const apiNftAddress = queryNftAddress.toLowerCase();
      setTradeAction({ type: "offload", tokenId: tokenIdText });
      setTradeStatus("loading");
      setTradeMessage(t(locale, "profileMsgOffloadSubmitting"));
      const { request: simulatedOffload } = await publicClient.simulateContract(
        {
          account: walletAddress,
          address: NFT_SALE_CONTRACT_ADDRESS,
          abi: nftSaleAbi,
          functionName: "offload",
          args: [queryNftAddress, tokenId],
        },
      );

      const offloadTx = await writeContractAsync({
        ...simulatedOffload,
      });

      await publicClient.waitForTransactionReceipt({
        hash: offloadTx,
        timeout: 120_000,
      });

      await apiNotifyShopTx({
        action: "offload",
        source: "profile-offload-token",
        nftAddress: apiNftAddress,
        tokenId: String(tokenId),
        txHash: offloadTx,
      });

      await queryClient.invalidateQueries({
        queryKey: [
          "profile",
          "nft-listings",
          walletAddress.toLowerCase(),
          apiNftAddress,
        ],
      });

      setTradeStatus("success");
      setTradeMessage(
        `Token #${tokenIdText} ${t(locale, "profileMsgOffloadSuccess")}${offloadTx}`,
      );
      await runHoldingsQuery();
    } catch (error) {
      setTradeStatus("error");
      setTradeMessage(
        `${t(locale, "profileMsgOffloadFailed")}${normalizeRpcError(error, locale)}`,
      );
    } finally {
      setTradeAction({ type: "", tokenId: "" });
    }
  };

  const runHoldingsQuery = useCallback(async () => {
    if (!walletAddress) {
      setHoldingsStatus("error");
      setHoldingsMessage(t(locale, "profileMsgConnectWalletFirst"));
      setTokenIds([]);
      return;
    }

    if (!isAddress(queryNftAddress)) {
      setHoldingsStatus("error");
      setHoldingsMessage(t(locale, "profileMsgInvalidNftAddress"));
      setTokenIds([]);
      return;
    }

    try {
      setHoldingsStatus("loading");
      setHoldingsMessage(t(locale, "profileMsgQuerying"));
      const apiNftAddress = queryNftAddress.toLowerCase();

      let walletTokenIds = [];
      let listedTokenIds = [];
      let auctionTokenIds = [];
      let walletCount = 0;
      let listedCount = 0;
      let chainQueryError = "";
      let listedQueryError = "";

      if (publicClient) {
        try {
          const balanceRaw = await publicClient.readContract({
            address: queryNftAddress,
            abi: chronosArtNftAbi,
            functionName: "balanceOf",
            args: [walletAddress],
          });

          walletCount = Number(balanceRaw || 0n);
          if (walletCount > 0) {
            let supportsEnumerable = false;
            try {
              supportsEnumerable = await publicClient.readContract({
                address: queryNftAddress,
                abi: ERC165_ABI,
                functionName: "supportsInterface",
                args: [ERC721_ENUMERABLE_INTERFACE_ID],
              });
            } catch {
              supportsEnumerable = false;
            }

            if (supportsEnumerable) {
              const maxFetch = 200;
              const fetchCount = Math.min(walletCount, maxFetch);
              const ids = [];
              for (let idx = 0; idx < fetchCount; idx += 1) {
                try {
                  const id = await publicClient.readContract({
                    address: queryNftAddress,
                    abi: ERC721_ENUMERABLE_ABI,
                    functionName: "tokenOfOwnerByIndex",
                    args: [walletAddress, BigInt(idx)],
                  });
                  ids.push(String(id));
                } catch {
                  chainQueryError = t(locale, "profileMsgEnumInterrupted");
                  break;
                }
              }
              walletTokenIds = ids;
            } else {
              chainQueryError = t(locale, "profileMsgEnumNotSupported");
            }
          }
        } catch (error) {
          chainQueryError = normalizeRpcError(error, locale);
        }
      }

      walletTokenIds = Array.from(
        new Set(
          walletTokenIds
            .map((tokenId) => String(tokenId || "").trim())
            .filter((tokenId) => /^\d+$/.test(tokenId)),
        ),
      );

      if (walletTokenIds.length > 0) {
        try {
          await apiSyncUserNftHoldings(walletAddress, {
            nftAddress: apiNftAddress,
            tokenIds: walletTokenIds,
            source: "profile-holdings-query",
          });
        } catch {
          // Best-effort sync only; holdings query should still continue.
        }
      }

      try {
        const listedResult = await queryClient.fetchQuery({
          queryKey: [
            "profile",
            "nft-listings",
            walletAddress.toLowerCase(),
            apiNftAddress,
          ],
          queryFn: () =>
            apiUserNftListings(walletAddress, {
              nftAddress: apiNftAddress,
              limit: 500,
            }),
          staleTime: 20_000,
        });
        const rawListingsArray = Array.isArray(listedResult?.listings)
          ? listedResult.listings
          : [];
        const listingsArray = rawListingsArray
          .map((listing) => {
            const tokenIdText = String(
              listing?.tokenId ??
                listing?.token_id ??
                String(listing?.id || "").split(":")[1] ??
                "",
            ).trim();
            return /^\d+$/.test(tokenIdText)
              ? { ...listing, tokenIdText }
              : null;
          })
          .filter(Boolean);

        const map = {};
        // Verify each listing on-chain via tokenSaleMeta for freshness
        const verifyResults =
          publicClient && listingsArray.length
            ? await publicClient.multicall({
                contracts: listingsArray.map((lst) => ({
                  address: NFT_SALE_CONTRACT_ADDRESS,
                  abi: nftSaleAbi,
                  functionName: "tokenSaleMeta",
                  args: [queryNftAddress, BigInt(lst.tokenIdText)],
                })),
                allowFailure: true,
              })
            : [];

        listingsArray.forEach((listing, idx) => {
          const tokenIdStr = String(listing?.tokenIdText || "").trim();
          if (!tokenIdStr) return;
          const onchain = verifyResults[idx];
          const backendStatus = String(
            listing?.listingStatus || "",
          ).toLowerCase();
          const backendSaysListed = backendStatus === "listed";
          const fallbackBackendPrice =
            listing?.listingPrice ??
            listing?.currentPrice ??
            listing?.lastSale ??
            null;

          // Primary rule: keep backend listed entries in holdings table.
          // On-chain data, when available and active, is only used to refresh price.
          if (backendSaysListed) {
            map[tokenIdStr] = {
              listingPrice:
                onchain?.status === "success" && onchain.result?.sale
                  ? formatEther(onchain.result.price)
                  : fallbackBackendPrice,
              listingStatus: "listed",
              listedAt: listing.listedAt,
              image: resolveNftImage(listing),
              galleryImages: resolveNftGalleryImages(listing),
              name: String(listing?.name || "").trim(),
            };
          }
        });
        setListingsMap(map);

        listedTokenIds = Object.keys(map);
        listedCount = listedTokenIds.length;
      } catch (error) {
        listedQueryError = normalizeRpcError(error, locale);
      }

      // --- Auction tokens: merge on-chain scan + backend for completeness ---
      const onchainAuctionMap = {};
      if (publicClient) {
        try {
          const maxId = Number(
            await publicClient.readContract({
              address: AUCTION_CONTRACT_ADDRESS,
              abi: auctionAbi,
              functionName: "auctionId",
            }),
          );
          if (maxId > 0) {
            const scanFrom = Math.max(1, maxId - 199); // scan last 200 auctions
            const now = Math.floor(Date.now() / 1000);
            const calls = [];
            for (let i = scanFrom; i <= maxId; i++) {
              calls.push({
                address: AUCTION_CONTRACT_ADDRESS,
                abi: auctionAbi,
                functionName: "auctionRecord",
                args: [BigInt(i)],
              });
            }
            const results = await publicClient.multicall({
              contracts: calls,
              allowFailure: true,
            });
            results.forEach((result, idx) => {
              if (result.status !== "success") return;
              const rec = result.result;
              if (
                rec.auctionOwner?.toLowerCase() !== walletAddress.toLowerCase()
              )
                return;
              if (rec.nft?.toLowerCase() !== queryNftAddress.toLowerCase())
                return;
              if (Number(rec.endTime) <= now) return; // ended/expired
              const tid = String(rec.tokenid);
              if (!tid) return;
              onchainAuctionMap[tid] = {
                auctionId: scanFrom + idx,
                currentBid: rec.lastPrice ? formatEther(rec.lastPrice) : null,
              };
            });
          }
        } catch {
          // non-fatal
        }
      }

      try {
        const auctionResult = await queryClient.fetchQuery({
          queryKey: ["profile", "auctions", walletAddress],
          queryFn: () =>
            apiGetUserAuctions(walletAddress, {
              status: "active",
              limit: 200,
            }),
          staleTime: 15_000,
        });
        const auctionsArray = Array.isArray(auctionResult?.auctions)
          ? auctionResult.auctions
          : [];
        const backendAuctionMap = {};
        auctionsArray.forEach((auction) => {
          const nftInRow = String(
            auction?.nftAddress ?? auction?.nft_address ?? auction?.nft ?? "",
          )
            .trim()
            .toLowerCase();
          if (nftInRow && nftInRow !== queryNftAddress.toLowerCase()) return;
          const tid = String(
            auction?.tokenId ?? auction?.token_id ?? auction?.tokenid ?? "",
          ).trim();
          if (tid) {
            backendAuctionMap[tid] = {
              auctionId:
                auction?.id || auction?.auctionId || auction?.auction_id,
              currentBid:
                auction?.highestBid ??
                auction?.highest_bid ??
                auction?.currentPrice ??
                auction?.current_price ??
                auction?.lastPrice ??
                auction?.last_price ??
                null,
              image: resolveNftImage(auction),
              galleryImages: resolveNftGalleryImages(auction),
              name: String(auction?.name || "").trim(),
            };
          }
        });
        const mergedAuctionMap = {
          ...backendAuctionMap,
          ...onchainAuctionMap,
        };
        setAuctionsMap(mergedAuctionMap);
        auctionTokenIds = Object.keys(mergedAuctionMap);
      } catch {
        setAuctionsMap(onchainAuctionMap);
        auctionTokenIds = Object.keys(onchainAuctionMap);
      }

      const mergedIds = Array.from(
        new Set([...walletTokenIds, ...listedTokenIds, ...auctionTokenIds]),
      );

      setTokenIds(mergedIds);
      setHoldingsStatus("success");
      if (!mergedIds.length) {
        const noDataHint =
          chainQueryError || listedQueryError
            ? t(locale, "profileMsgPartialDataIssue")
            : "";
        setHoldingsMessage(`${t(locale, "profileMsgNoNftData")}${noDataHint}`);
      } else {
        let msg = `${t(locale, "profileMsgQuerySummaryPrefix")} ${walletCount}${t(locale, "profileMsgQuerySummaryMiddle1")} ${listedCount}${t(locale, "profileMsgQuerySummaryMiddle2")} ${auctionTokenIds.length}${t(locale, "profileMsgQuerySummaryMiddle3")} ${mergedIds.length}${t(locale, "profileMsgQuerySummarySuffix")}`;
        if (chainQueryError) {
          msg += ` ${t(locale, "profileMsgChainQueryError")}${chainQueryError}`;
        }
        if (listedQueryError) {
          msg += ` ${t(locale, "profileMsgListedQueryError")}${listedQueryError}`;
        }
        setHoldingsMessage(msg);
      }
    } catch (error) {
      setHoldingsStatus("error");
      setHoldingsMessage(
        `${t(locale, "profileMsgQueryFailed")}${normalizeRpcError(error, locale)}`,
      );
      setTokenIds([]);
    }
  }, [walletAddress, queryNftAddress, publicClient, locale, queryClient]);

  useEffect(() => {
    if (!walletConnected || !walletAddress || !publicClient) return;
    if (!isAddress(queryNftAddress)) return;

    const autoQueryKey = `${walletAddress.toLowerCase()}-${queryNftAddress.toLowerCase()}`;
    if (autoHoldingsQueryKeyRef.current === autoQueryKey) return;

    autoHoldingsQueryKeyRef.current = autoQueryKey;
    runHoldingsQuery();
  }, [
    walletConnected,
    walletAddress,
    publicClient,
    queryNftAddress,
    runHoldingsQuery,
  ]);

  const runCheckin = async () => {
    if (!walletConnected) {
      setStatus("error");
      setMessage(t(locale, "profileMsgCheckinLoginFirst"));
      return;
    }

    // Check if already checked in today
    if (alreadyCheckedInToday) {
      setStatus("success");
      setMessage(
        `${t(locale, "profileMsgCheckinAlreadyToday")} ${dashboard.streak || 1} ${t(locale, "profileMsgCheckinNoNeed")}`,
      );
      return;
    }

    try {
      setStatus("loading");
      setMessage(t(locale, "profileMsgCheckinSending"));

      if (!publicClient) {
        throw new Error(t(locale, "profileMsgChainClientUnavailable"));
      }

      const gasPriceToUse = await publicClient.getGasPrice();
      let gasLimitToUse = 300000n;

      try {
        const estimatedGas = await publicClient.estimateContractGas({
          account: walletAddress,
          address: CHECKIN_CONTRACT_ADDRESS,
          abi: CHECKIN_CONTRACT_ABI,
          functionName: "checkIn",
        });
        gasLimitToUse = (estimatedGas * 12n) / 10n;
      } catch {
        gasLimitToUse = 300000n;
      }

      const txHash = await writeContractAsync({
        account: walletAddress,
        address: CHECKIN_CONTRACT_ADDRESS,
        abi: CHECKIN_CONTRACT_ABI,
        functionName: "checkIn",
        gas: gasLimitToUse,
        gasPrice: gasPriceToUse,
      });

      setMessage(
        `${t(locale, "profileMsgTxSubmitted")}${txHash}${t(locale, "profileMsgWaitingConfirm")}`,
      );
      await publicClient.waitForTransactionReceipt({ hash: txHash });

      const result = await runCheckinApi({ txHash });
      setStatus("success");
      if (result?.alreadyCheckedIn) {
        setMessage(t(locale, "profileMsgAlreadyCheckedInNoNeed"));
      } else {
        setMessage(
          `${t(locale, "checkinSuccess")}${t(locale, "profileMsgCheckinStreakPrefix")}${result?.streak || 1}${t(locale, "profileMsgCheckinStreakSuffix")}${result?.txHash || txHash}`,
        );
      }
    } catch (error) {
      setStatus("error");
      setMessage(
        `${t(locale, "checkinFailed")} (${normalizeRpcError(error, locale)})`,
      );
    }
  };

  const runCreateAuction = async (
    tokenIdInput,
    { startPrice = "", durationHours = "12h" } = {},
  ) => {
    if (isAuctionProcessing) return;
    if (!walletConnected || !walletAddress) {
      setAuctionFeedback("error", t(locale, "auctionMsgConnectWallet"));
      return false;
    }
    if (!isAddress(queryNftAddress)) {
      setAuctionFeedback("error", t(locale, "auctionFormInvalidNft"));
      return false;
    }
    if (!publicClient) {
      setAuctionFeedback("error", t(locale, "profileMsgRpcUnavailableShort"));
      return false;
    }
    const tokenIdText = String(tokenIdInput || "").trim();
    if (!tokenIdText) {
      setAuctionFeedback("error", t(locale, "auctionFormInvalidToken"));
      return false;
    }
    const listing = listingsMap[tokenIdText];
    const inMarket = Boolean(
      listing && String(listing.listingStatus || "").toLowerCase() === "listed",
    );
    const inAuction = Boolean(auctionsMap[tokenIdText]);
    if (inMarket || inAuction) {
      setAuctionFeedback("error", t(locale, "auctionFormInvalidToken"));
      return false;
    }

    const startPriceInput = String(startPrice || "").trim();
    let startPriceWeiCheck = 0n;
    try {
      startPriceWeiCheck = parseEther(startPriceInput);
    } catch {
      startPriceWeiCheck = 0n;
    }
    if (!startPriceInput || startPriceWeiCheck <= 0n) {
      setAuctionFeedback("error", t(locale, "auctionFormInvalidStartPrice"));
      return false;
    }

    setAuctionAction({ type: "create", auctionId: "", tokenId: tokenIdText });
    try {
      const nftAddress = queryNftAddress.trim();
      const tokenId = BigInt(tokenIdText);
      const expectedQuote = normalizeLowerAddress(USDT_CONTRACT_ADDRESS);

      // setApprovalForAll: one-time approval per NFT contract, more efficient than per-token approve
      const isApproved = await publicClient.readContract({
        address: nftAddress,
        abi: chronosArtNftAbi,
        functionName: "isApprovedForAll",
        args: [walletAddress, AUCTION_CONTRACT_ADDRESS],
      });

      if (!isApproved) {
        setAuctionFeedback("loading", t(locale, "auctionApproving"));
        const approveTx = await writeContractAsync({
          account: walletAddress,
          address: nftAddress,
          abi: chronosArtNftAbi,
          functionName: "setApprovalForAll",
          args: [AUCTION_CONTRACT_ADDRESS, true],
        });
        await publicClient.waitForTransactionReceipt({
          hash: approveTx,
          timeout: 60_000,
        });
      }

      setAuctionFeedback("loading", t(locale, "auctionFormSubmitting"));

      const startTime = Math.floor(Date.now() / 1000) + 60;
      const durationSec = durationHours === "24h" ? 86400 : 43200;
      const endTime = startTime + durationSec;
      const callPeroid = 3600; // extend if bid in last 1h
      const postponePeroid = BigInt(600); // extend by 10min
      try {
        const quoteAllowed = await publicClient.readContract({
          address: AUCTION_CONTRACT_ADDRESS,
          abi: auctionAbi,
          functionName: "quoteWhitelist",
          args: [USDT_CONTRACT_ADDRESS],
        });
        if (!quoteAllowed) {
          setAuctionFeedback(
            "error",
            t(
              locale,
              "auctionQuoteNotWhitelisted",
              `USDT quote is not whitelisted on auction contract. expected=${expectedQuote}`,
            ),
          );
          return false;
        }
      } catch (whitelistError) {
        console.warn(
          "[auction/create] read quoteWhitelist failed, continue creating",
          whitelistError,
        );
      }
      const txArgs = [
        nftAddress,
        tokenId,
        parseEther(startPriceInput),
        1000,
        BigInt(startTime),
        BigInt(endTime),
        callPeroid,
        USDT_CONTRACT_ADDRESS,
        postponePeroid,
      ];
      const { request: simulatedRequest } = await publicClient.simulateContract(
        {
          account: walletAddress,
          address: AUCTION_CONTRACT_ADDRESS,
          abi: auctionAbi,
          functionName: "newAuction",
          args: txArgs,
        },
      );
      const txHash = await writeContractAsync({
        ...simulatedRequest,
      });

      await publicClient.waitForTransactionReceipt({
        hash: txHash,
        timeout: 120_000,
      });

      // Verify the created auction quote directly from chain to avoid UI/backend drift.
      const latestAuctionId = await publicClient.readContract({
        address: AUCTION_CONTRACT_ADDRESS,
        abi: auctionAbi,
        functionName: "auctionId",
      });
      let createdQuote = "";
      try {
        const createdRecord = await publicClient.readContract({
          address: AUCTION_CONTRACT_ADDRESS,
          abi: auctionAbi,
          functionName: "auctionRecord",
          args: [latestAuctionId],
        });
        createdQuote = String(
          createdRecord?.bidQuote ?? createdRecord?.[10] ?? "",
        )
          .trim()
          .toLowerCase();
      } catch (verifyErr) {
        console.warn(
          "[auction/create] failed to verify quote from chain",
          verifyErr,
        );
      }

      try {
        await apiNotifyAuctionTx({
          txHash,
          action: "new",
          source: "profile-create-auction",
        });
      } catch (notifyError) {
        console.warn(
          "[auction/notify] new auction enqueue failed",
          notifyError,
        );
      }

      if (createdQuote && createdQuote !== expectedQuote) {
        setAuctionFeedback(
          "error",
          `${t(locale, "auctionFormSuccess")} #${String(latestAuctionId)} | quote: ${createdQuote} | expected: ${expectedQuote}`,
        );
      } else {
        setAuctionFeedback(
          "success",
          `${t(locale, "auctionFormSuccess")} #${String(latestAuctionId)} | quote: ${createdQuote || expectedQuote}`,
        );
      }
      queryClient.invalidateQueries({
        queryKey: ["profile", "auctions", walletAddress],
      });
      await runHoldingsQuery();
      return true;
    } catch (error) {
      console.error("Auction creation failed:", error);
      setAuctionFeedback(
        "error",
        `${t(locale, "auctionFormFailed")}${toErrorMessage(error)}`,
      );
      return false;
    } finally {
      setAuctionAction({ type: "", auctionId: "", tokenId: "" });
    }
  };

  const runCancelAuction = async (auctionId, tokenIdInput = "") => {
    if (isAuctionProcessing) return;
    if (!walletConnected || !walletAddress)
      return setAuctionFeedback("error", t(locale, "auctionMsgConnectWallet"));
    if (!publicClient)
      return setAuctionFeedback(
        "error",
        t(locale, "profileMsgRpcUnavailableShort"),
      );

    try {
      setAuctionAction({
        type: "cancel",
        auctionId: String(auctionId),
        tokenId: String(tokenIdInput || "").trim(),
      });
      setAuctionFeedback("loading", t(locale, "auctionCancelSubmitting"));

      const auctionIdBig = BigInt(String(auctionId));

      // Read cancelFeePercent and auctionRecord in parallel to compute required cancel fee
      const [feePercentRaw, record] = await Promise.all([
        publicClient.readContract({
          address: AUCTION_CONTRACT_ADDRESS,
          abi: auctionAbi,
          functionName: "cancelFeePercent",
        }),
        publicClient.readContract({
          address: AUCTION_CONTRACT_ADDRESS,
          abi: auctionAbi,
          functionName: "auctionRecord",
          args: [auctionIdBig],
        }),
      ]);

      // Explicit BigInt conversion to avoid "Cannot mix BigInt and other types"
      const feePercent = BigInt(feePercentRaw ?? 0);
      // viem may return named props OR positional array, use both as fallback.
      // auctionRecord output order: nft[0], tokenid[1], bidIncrement[2], startTime[3],
      // endTime[4], callPeroid[5], startPrice[6], lastPrice[7], auctionOwner[8], ...
      const startPrice = BigInt(record?.startPrice ?? record?.[6] ?? 0);
      const lastPrice = BigInt(record?.lastPrice ?? record?.[7] ?? 0);
      // Use lastPrice (current highest bid) if there is one, otherwise startPrice
      const priceBase = lastPrice > 0n ? lastPrice : startPrice;
      // cancelFeePercent may be plain percent (e.g. 5 -> 5%) or basis points (e.g. 500 -> 5%).
      const feeDivisor = feePercent > 100n ? 10000n : 100n;
      const cancelFee =
        feePercent > 0n ? (priceBase * feePercent + feeDivisor - 1n) / feeDivisor : 0n;
      const quoteAddress = USDT_CONTRACT_ADDRESS;

      const ensureQuoteAllowance = async (targetAmount) => {
        if (!targetAmount || targetAmount <= 0n) return;

        const allowance = await publicClient.readContract({
          address: quoteAddress,
          abi: ERC20_ABI,
          functionName: "allowance",
          args: [walletAddress, AUCTION_CONTRACT_ADDRESS],
        });
        if (allowance >= targetAmount) return;

        const waitTxSuccess = async (hash) => {
          const receipt = await publicClient.waitForTransactionReceipt({
            hash,
            timeout: 120_000,
          });
          if (receipt?.status !== "success") {
            throw new Error("approve transaction reverted");
          }
        };

        const approveSpender = async (amount) => {
          const approveHash = await writeContractAsync({
            address: quoteAddress,
            abi: ERC20_ABI,
            functionName: "approve",
            args: [AUCTION_CONTRACT_ADDRESS, amount],
          });
          await waitTxSuccess(approveHash);
        };

        setAuctionFeedback(
          "loading",
          t(locale, "marketMsgApprovingToken", "Approving USDT spend..."),
        );
        const approveAmount =
          targetAmount > USDT_CANCEL_APPROVAL_WEI
            ? targetAmount
            : USDT_CANCEL_APPROVAL_WEI;

        try {
          await approveSpender(approveAmount);
        } catch (approveError) {
          // Some ERC20 tokens require setting allowance to 0 before changing it.
          if (allowance > 0n) {
            await approveSpender(0n);
            await approveSpender(approveAmount);
          } else {
            throw approveError;
          }
        }

        const refreshedAllowance = await publicClient.readContract({
          address: quoteAddress,
          abi: ERC20_ABI,
          functionName: "allowance",
          args: [walletAddress, AUCTION_CONTRACT_ADDRESS],
        });
        if (refreshedAllowance < targetAmount) {
          throw new Error(
            `Quote allowance insufficient after approve: ${refreshedAllowance.toString()} < ${targetAmount.toString()}`,
          );
        }
      };
      await ensureQuoteAllowance(cancelFee > 0n ? cancelFee : priceBase);

      setAuctionFeedback("loading", t(locale, "auctionCancelSubmitting"));
      const { request: simulatedCancel } = await publicClient.simulateContract({
        account: walletAddress,
        address: AUCTION_CONTRACT_ADDRESS,
        abi: auctionAbi,
        functionName: "cancel",
        args: [auctionIdBig],
      });

      const txHash = await writeContractAsync({ ...simulatedCancel });
      await publicClient.waitForTransactionReceipt({ hash: txHash });
      try {
        await apiNotifyAuctionTx({
          txHash,
          auctionId: String(auctionId),
          action: "cancel",
          source: "profile-cancel-auction",
        });
      } catch (notifyError) {
        console.warn("[auction/notify] cancel enqueue failed", notifyError);
      }
      setAuctionFeedback("success", t(locale, "auctionCancelSuccess"));
      // Optimistic local update so cancelled token becomes selectable immediately.
      setAuctionsMap((prev) => {
        const next = {};
        Object.entries(prev || {}).forEach(([tokenId, meta]) => {
          if (String(meta?.auctionId) !== String(auctionId)) {
            next[tokenId] = meta;
          }
        });
        return next;
      });
      queryClient.invalidateQueries({
        queryKey: ["profile", "auctions", walletAddress],
      });
      await runHoldingsQuery();
    } catch (error) {
      setAuctionFeedback(
        "error",
        `${t(locale, "auctionCancelFailed")}${toErrorMessage(error)}`,
      );
    } finally {
      setAuctionAction({ type: "", auctionId: "", tokenId: "" });
    }
  };

  const runDelistToken = async (tokenIdInput) => {
    const tokenIdText = String(tokenIdInput || "").trim();
    if (!tokenIdText) return;

    const auction = auctionsMap[tokenIdText];
    const listing = listingsMap[tokenIdText];
    const isListedInMarket = Boolean(
      listing && String(listing.listingStatus || "").toLowerCase() === "listed",
    );

    if (auction?.auctionId) {
      await runCancelAuction(auction.auctionId, tokenIdText);
      return;
    }
    if (isListedInMarket) {
      await runOffloadToken(tokenIdText);
      return;
    }

    setTradeStatus("error");
    setTradeMessage(t(locale, "profileMsgNoNftData"));
  };

  const openListingModal = useCallback((mode, tokenIdInput) => {
    const tokenId = String(tokenIdInput || "").trim();
    if (!tokenId) return;
    setListingModal({
      open: true,
      mode: mode === "auction" ? "auction" : "market",
      tokenId,
      price: "",
      durationHours: "12h",
    });
  }, []);

  const closeListingModal = useCallback(() => {
    setListingModal({
      open: false,
      mode: "",
      tokenId: "",
      price: "",
      durationHours: "12h",
    });
  }, []);

  const isListingModalSubmitting =
    listingModal.mode === "auction"
      ? isAuctionProcessing &&
        auctionAction.type === "create" &&
        auctionAction.tokenId === listingModal.tokenId
      : isTradeProcessing &&
        tradeAction.type === "list" &&
        tradeAction.tokenId === listingModal.tokenId;

  const listingModalTokenCatalog = nftCatalogByTokenId[listingModal.tokenId];
  const listingModalTokenListing = listingsMap[listingModal.tokenId];
  const listingModalTokenAuction = auctionsMap[listingModal.tokenId];
  const listingModalImage =
    listingModalTokenListing?.image ||
    listingModalTokenAuction?.image ||
    listingModalTokenCatalog?.image ||
    "";
  const listingModalTitle =
    listingModalTokenListing?.name ||
    listingModalTokenAuction?.name ||
    listingModalTokenCatalog?.name ||
    `${t(locale, "detailTokenId", "Token ID")} #${listingModal.tokenId || "-"}`;

  const runConfirmListingModal = async () => {
    if (!listingModal.open || isListingModalSubmitting) return;
    const tokenIdText = String(listingModal.tokenId || "").trim();
    const priceText = String(listingModal.price || "").trim();
    if (!tokenIdText) return;

    if (listingModal.mode === "auction") {
      const success = await runCreateAuction(tokenIdText, {
        startPrice: priceText,
        durationHours: listingModal.durationHours,
      });
      if (success) closeListingModal();
      return;
    }

    const success = await runListToken(tokenIdText, priceText);
    if (success) closeListingModal();
  };

  return (
    <>
      <Seo
        locale={locale}
        title={`Chronos Arte Node | ${t(locale, "navProfile")}`}
        description={t(locale, "profileSeoDesc")}
        path={buildPath(locale, "profile")}
      />

      <Panel
        title={t(locale, "navProfile")}
        description={t(locale, "profilePanelDesc")}
      ></Panel>

      <Panel
        title={t(locale, "profileCheckinTitle")}
        description={t(locale, "profileCheckinDesc")}
      >
        {walletConnected && (
          <div
            className="card-grid three-cols"
            style={{ marginBottom: "16px" }}
          >
            <article className="card-link" style={{ pointerEvents: "none" }}>
              <h3>{t(locale, "profileStreak")}</h3>
              <p>
                {dashboard?.streak || 0} {t(locale, "profileDays")}
              </p>
            </article>
            <article className="card-link" style={{ pointerEvents: "none" }}>
              <h3>{t(locale, "profileTodayStatus")}</h3>
              <p>
                {alreadyCheckedInToday
                  ? t(locale, "profileCheckedIn")
                  : t(locale, "profileNotCheckedIn")}
              </p>
            </article>
            <article className="card-link" style={{ pointerEvents: "none" }}>
              <h3>{t(locale, "profileTotalPoints")}</h3>
              <p>{dashboard?.points || 0}</p>
            </article>
          </div>
        )}

        <div className="tool-row">
          <button
            className="btn btn-primary"
            onClick={runCheckin}
            disabled={alreadyCheckedInToday || isCheckinProcessing}
          >
            {alreadyCheckedInToday
              ? t(locale, "profileCheckinBtnDone")
              : isCheckinProcessing
                ? t(locale, "profileCheckinBtnLoading")
                : t(locale, "profileCheckinBtnNow")}
          </button>
        </div>

        {status !== "idle" && message && (
          <div className={`status-banner ${status}`}>
            {status === "loading" && (
              <strong>{t(locale, "profileStatusProcessing")}</strong>
            )}
            {status === "success" && (
              <strong>{t(locale, "profileStatusSuccess")}</strong>
            )}
            {status === "error" && (
              <strong>{t(locale, "profileStatusError")}</strong>
            )}
            <p>{message}</p>
          </div>
        )}
      </Panel>

      <Panel
        title={t(locale, "profileCheckinLogTitle")}
        description={t(locale, "profileCheckinLogDesc")}
      >
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t(locale, "profileTime")}</th>
                <th>{t(locale, "profileStatus")}</th>
                <th>{t(locale, "profileReason")}</th>
                <th>{t(locale, "profilePoints")}</th>
              </tr>
            </thead>
            <tbody>
              {checkinLog.length ? (
                checkinLog.map((row, idx) => (
                  <tr key={`${row.time}-${idx}`}>
                    <td>{row.time}</td>
                    <td>{row.status}</td>
                    <td>{row.reason}</td>
                    <td>{row.points || 0}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="empty-state">
                    {t(locale, "profileNoCheckinRecords")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel
        title={t(locale, "profileHoldingsTitle")}
        description={t(locale, "profileHoldingsDesc")}
      >
        <div className="form-grid">
          <label>
            {t(locale, "profileNftContractAddress")}
            <input
              value={queryNftAddress}
              onChange={(e) => setQueryNftAddress(e.target.value.trim())}
              placeholder="0x..."
            />
          </label>
          <label>
            {t(locale, "profileCurrentWallet")}
            <input
              value={walletAddress || t(locale, "profileWalletNotConnected")}
              readOnly
            />
          </label>
        </div>

        {holdingsStatus !== "idle" && holdingsMessage && (
          <div className={`status-banner ${holdingsStatus}`}>
            {holdingsStatus === "loading" && (
              <strong>{t(locale, "profileQueryLoading")}</strong>
            )}
            {holdingsStatus === "success" && (
              <strong>{t(locale, "profileQuerySuccess")}</strong>
            )}
            {holdingsStatus === "error" && (
              <strong>{t(locale, "profileQueryError")}</strong>
            )}
            <p>{holdingsMessage}</p>
          </div>
        )}

        {tradeStatus !== "idle" && tradeMessage && (
          <div className={`status-banner ${tradeStatus}`}>
            {tradeStatus === "loading" && (
              <strong>{t(locale, "profileStatusProcessing")}</strong>
            )}
            {tradeStatus === "success" && (
              <strong>{t(locale, "profileStatusSuccess")}</strong>
            )}
            {tradeStatus === "error" && (
              <strong>{t(locale, "profileStatusError")}</strong>
            )}
            <p>{tradeMessage}</p>
          </div>
        )}
        {auctionStatus !== "idle" && auctionMessage && (
          <div className={`status-banner ${auctionStatus}`}>
            {auctionStatus === "loading" && (
              <strong>{t(locale, "auctionStatusProcessing")}</strong>
            )}
            {auctionStatus === "success" && (
              <strong>{t(locale, "auctionStatusSuccess")}</strong>
            )}
            {auctionStatus === "error" && (
              <strong>{t(locale, "auctionStatusError")}</strong>
            )}
            <p>{auctionMessage}</p>
          </div>
        )}

        {tokenIds.length ? (
          <div className="nft-card-grid profile-holdings-grid">
            {tokenIds.map((id, idx) => {
              const listing = listingsMap[id];
              const auction = auctionsMap[id];
              const catalog = nftCatalogByTokenId[id];
              const isListed = Boolean(
                listing && listing.listingStatus === "listed",
              );
              const isInAuction = Boolean(auction);
              const listingQuoteSymbol = AUCTION_QUOTE_SYMBOL;
              const auctionQuoteSymbol = AUCTION_QUOTE_SYMBOL;
              const displayPrice = isListed
                ? formatAmount(listing?.listingPrice, listingQuoteSymbol)
                : "-";
              const displayBid = isInAuction
                ? formatAmount(auction?.currentBid, auctionQuoteSymbol)
                : "-";
              const assetTypeLabel = isInAuction
                ? t(locale, "profileAssetTypeAuction")
                : isListed
                  ? t(locale, "profileAssetTypeListed")
                  : t(locale, "profileAssetTypeOwned");
              const image =
                listing?.image || auction?.image || catalog?.image || "";
              const galleryImages =
                listing?.galleryImages ||
                auction?.galleryImages ||
                catalog?.galleryImages ||
                [];
              const cardTitle =
                listing?.name ||
                auction?.name ||
                catalog?.name ||
                `${t(locale, "detailTokenId", "Token ID")} #${id}`;
              const cardPrice = isInAuction
                ? `${displayBid} (${t(locale, "auctionTypeEnglish")} #${auction?.auctionId ?? "-"})`
                : isListed
                  ? `${displayPrice} (${t(locale, "profileListed")})`
                  : "-";
              const badgeTone = isInAuction
                ? "warn"
                : isListed
                  ? "success"
                  : "default";

              return (
                <NftCard
                  key={`${id}-${idx}`}
                  locale={locale}
                  className="profile-holding-card"
                  image={image}
                  galleryImages={galleryImages}
                  title={cardTitle}
                  tokenId={id}
                  nftAddress={queryNftAddress}
                  resolveTokenName
                  price={cardPrice}
                  priceLabel={t(locale, "profileListingPrice")}
                  badge={assetTypeLabel}
                  badgeTone={badgeTone}
                >
                  <div className="tool-row nft-card-action-row">
                    {isInAuction || isListed ? (
                      <button
                        className="btn btn-ghost"
                        onClick={() => runDelistToken(id)}
                        disabled={
                          isTradeProcessing ||
                          isAuctionProcessing ||
                          (isInAuction &&
                            auctionAction.type === "cancel" &&
                            String(auctionAction.auctionId) ===
                              String(auction?.auctionId)) ||
                          (isListed &&
                            tradeAction.type === "offload" &&
                            tradeAction.tokenId === id)
                        }
                      >
                        {isInAuction &&
                        auctionAction.type === "cancel" &&
                        String(auctionAction.auctionId) ===
                          String(auction?.auctionId)
                          ? t(locale, "auctionCancelSubmitting")
                          : isListed &&
                              tradeAction.type === "offload" &&
                              tradeAction.tokenId === id
                            ? t(locale, "profileOffloadBtnLoading")
                            : t(locale, "auctionCancelBtn")}
                      </button>
                    ) : (
                      <>
                        <button
                          className="btn btn-ghost"
                          onClick={() => openListingModal("auction", id)}
                          disabled={isTradeProcessing || isAuctionProcessing}
                        >
                          {isAuctionProcessing &&
                          auctionAction.type === "create" &&
                          auctionAction.tokenId === id
                            ? t(locale, "auctionFormSubmitting")
                            : `${t(locale, "navAuction", "Auction")} ${t(locale, "profileListBtn", "List")}`}
                        </button>
                        <button
                          className="btn btn-primary"
                          onClick={() => openListingModal("market", id)}
                          disabled={isTradeProcessing || isAuctionProcessing}
                        >
                          {isTradeProcessing &&
                          tradeAction.type === "list" &&
                          tradeAction.tokenId === id
                            ? t(locale, "profileListBtnLoading")
                            : `${t(locale, "navMarket", "Market")} ${t(locale, "profileListBtn", "List")}`}
                        </button>
                      </>
                    )}
                  </div>
                </NftCard>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">{t(locale, "profileMsgNoNftData")}</div>
        )}
      </Panel>

      {listingModal.open ? (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal-card profile-listing-modal-card">
            <div className="modal-head">
              <h3>
                {listingModal.mode === "auction"
                  ? `${t(locale, "navAuction", "Auction")} ${t(locale, "profileListBtn", "List")}`
                  : `${t(locale, "navMarket", "Market")} ${t(locale, "profileListBtn", "List")}`}
              </h3>
              <button
                className="btn btn-ghost"
                onClick={closeListingModal}
                disabled={isListingModalSubmitting}
              >
                {t(locale, "auctionHistoryClose", "Close")}
              </button>
            </div>

            <div className="profile-listing-modal-body">
              <div className="profile-listing-modal-asset">
                <div className="profile-listing-modal-image-wrap">
                  {listingModalImage ? (
                    <img
                      src={listingModalImage}
                      alt={listingModalTitle}
                      className="profile-listing-modal-image"
                    />
                  ) : (
                    <div className="profile-listing-modal-image-fallback">
                      {t(locale, "detailNoImage", "No image")}
                    </div>
                  )}
                </div>
                <div className="profile-listing-modal-asset-meta">
                  <p className="profile-listing-modal-title">
                    {listingModalTitle}
                  </p>
                  <p className="profile-listing-modal-token">
                    {t(locale, "detailTokenId", "Token ID")}: #
                    {listingModal.tokenId || "-"}
                  </p>
                </div>
              </div>

              <div className="profile-listing-modal-form">
                {listingModal.mode === "auction" ? (
                  <div className="profile-listing-modal-duration">
                    <span>{t(locale, "auctionFormDuration")}</span>
                    <label>
                      <input
                        type="radio"
                        name="profile-auction-duration-modal"
                        value="12h"
                        checked={listingModal.durationHours === "12h"}
                        onChange={(e) =>
                          setListingModal((prev) => ({
                            ...prev,
                            durationHours: e.target.value,
                          }))
                        }
                        disabled={isListingModalSubmitting}
                      />
                      {t(
                        locale,
                        "auctionDuration12H",
                        t(locale, "auctionDuration3M"),
                      )}
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="profile-auction-duration-modal"
                        value="24h"
                        checked={listingModal.durationHours === "24h"}
                        onChange={(e) =>
                          setListingModal((prev) => ({
                            ...prev,
                            durationHours: e.target.value,
                          }))
                        }
                        disabled={isListingModalSubmitting}
                      />
                      {t(
                        locale,
                        "auctionDuration24H",
                        t(locale, "auctionDuration6M"),
                      )}
                    </label>
                  </div>
                ) : null}

                <label className="profile-listing-modal-field">
                  {listingModal.mode === "auction"
                    ? t(locale, "auctionFormStartPrice")
                    : t(locale, "profileListingPrice")}
                  <input
                    type="number"
                    step="0.000001"
                    min="0"
                    value={listingModal.price}
                    onChange={(e) =>
                      setListingModal((prev) => ({
                        ...prev,
                        price: e.target.value,
                      }))
                    }
                    placeholder="0"
                    disabled={isListingModalSubmitting}
                  />
                </label>
                <p className="profile-listing-modal-hint">
                  {AUCTION_QUOTE_SYMBOL}
                </p>

                <div className="tool-row profile-listing-modal-actions">
                  <button
                    className="btn btn-ghost"
                    onClick={closeListingModal}
                    disabled={isListingModalSubmitting}
                  >
                    {t(locale, "auctionHistoryClose", "Close")}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={runConfirmListingModal}
                    disabled={isListingModalSubmitting}
                  >
                    {isListingModalSubmitting
                      ? listingModal.mode === "auction"
                        ? t(locale, "auctionFormSubmitting")
                        : t(locale, "profileListBtnLoading")
                      : listingModal.mode === "auction"
                        ? `${t(locale, "navAuction", "Auction")} ${t(locale, "profileListBtn", "List")}`
                        : `${t(locale, "navMarket", "Market")} ${t(locale, "profileListBtn", "List")}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
